import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { Button } from 'src/registry/ui/Button';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { PLAN_CHANGE_STATUS, Subscription } from 'src/features/subscriptions/utils/types';
import { useCancelSubscriptionPlanChange } from 'src/features/subscriptions/api/useCancelSubscriptionPlanChange';
import { LIST_SUBSCRIPTIONS_RESOURCE } from 'src/features/subscriptions/api/useListSubscriptions';
import { SUBSCRIPTION_RESOURCE } from 'src/features/subscriptions/api/useGetSubscription';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';

type CancelPlanChangeModalProps = {
    subscription: Subscription;
    children?: React.ReactNode;
};

/*
 * Lives here rather than in its own file: it exists only for this banner and has no other caller.
 */
function CancelPlanChangeModal({ subscription, children }: CancelPlanChangeModalProps) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: cancelPlanChange } = useCancelSubscriptionPlanChange({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTIONS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(subscriptionsMessages.cancelPlanChangeSuccess),
                description: formatMessage(subscriptionsMessages.cancelPlanChangeSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorDetails || formatMessage(generalErrorMessages.unknownError),
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...subscriptionsMessages.cancelPlanChange },
                subTitle: { ...subscriptionsMessages.schedulePlanChangeSubtitle },
                icon: ICON_MAP.X_MARK,
                confirmationMessage: { ...subscriptionsMessages.confirmCancelPlanChange },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: subscriptionsMessages.cancelPlanChange,
                callbackFn: () => {
                    return (
                        cancelPlanChange({
                            subscriptionId: subscription.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

type ScheduledPlanChangeBannerProps = {
    subscription: Subscription;
    canManage: boolean;
};

/**
 * Shown only while a change is still pending. `applied`, `cancelled` and `failed` are terminal
 * history rather than something to act on - the dashboard detail panel carries those.
 */
function ScheduledPlanChangeBanner({ subscription, canManage }: ScheduledPlanChangeBannerProps) {
    const { formatMessage, formatDate } = useIntl();
    const change = subscription.scheduled_plan_change;

    if (!change || change.status !== PLAN_CHANGE_STATUS.SCHEDULED) return null;

    return (
        <div className="flex items-center justify-between gap-4 rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
            <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={ICON_MAP.SCHEDULED} className="text-orange-600" />
                <span className="text-sm">
                    {formatMessage(subscriptionsMessages.scheduledPlanChangeBanner, {
                        plan: change.target_service_plan.name,
                        date: formatDate(change.effective_date, { dateStyle: 'long' }),
                    })}
                </span>
            </div>
            {canManage && (
                <CancelPlanChangeModal subscription={subscription}>
                    <Button size="sm" variant="outline" className="cursor-pointer">
                        {formatMessage(subscriptionsMessages.cancelPlanChange)}
                    </Button>
                </CancelPlanChangeModal>
            )}
        </div>
    );
}

export default ScheduledPlanChangeBanner;
